<template>
  <work-record v-if="track" :key="track.slug" v-bind="record" class="music-room">
    <template #heading>
      <nav class="room-back" :aria-label="zh ? '音樂' : 'Music'"><router-link class="back-link work-back" :to="record.backPath">← {{ record.labels.back }}</router-link></nav>
    </template>
    <template #lead>
      <!-- 聽歌的房間（2026-10-04 第二版）：第一屏就有歌名、封面、播放器與整首詩。桌機左欄封面＋播放，右欄歌名＋詩；
           手機頂部「封面縮圖＋歌名」一列，下面整寬播放列，再下面是詩。版型與理由 → docs/MUSIC.md -->
      <section class="room" :class="'room-' + (track.text?.kind || 'bare')" :aria-label="zh ? '聽這首歌' : 'Listen to this piece'">
        <header class="room-head">
          <p class="work-kicker"><span>{{ record.category }}</span><span>{{ track.year }}</span></p>
          <h1 :lang="titleLang" :class="{ 'room-title-long': titleLong }"><span v-if="titleCode" class="room-code">{{ titleCode + ' ' }}</span>{{ titleRest }}</h1>
          <p v-if="record.subtitle" class="work-subtitle">{{ record.subtitle }}</p>
        </header>
        <div class="room-side">
          <figure class="room-cover" data-first-view="cover" :style="coverBox">
            <img v-if="track.cover" class="vt-work-media" :src="track.cover.src" :width="track.cover.width" :height="track.cover.height" :alt="coverAlt" fetchpriority="high" decoding="async" />
          </figure>
          <track-player class="room-player" :key="track.slug" :slug="track.slug" :title="record.title" :original="track.title.original" :seconds="track.seconds" :embed-url="track.embedUrl" :audio="audio" :artwork="artwork" :next="upNext" :zh="zh" />
          <p class="room-when">
            <time v-if="track.date" :datetime="track.date">{{ day(track.date) }}</time>
            <span>{{ length }}</span>
            <span v-if="track.genre">{{ track.genre }}</span>
          </p>
          <p class="room-links">
            <a :href="track.url" target="_blank" rel="noopener noreferrer" @click="listen('streetvoice')">StreetVoice <span aria-hidden="true">↗︎</span></a>
            <a v-for="s in track.streaming" :key="s.url" :href="s.url" target="_blank" rel="noopener noreferrer" @click="listen(s.name.toLowerCase())">{{ s.name }} <span aria-hidden="true">↗︎</span></a>
          </p>
          <p v-if="coverNote" class="room-cover-note">{{ coverNote }}</p>
        </div>
        <div class="room-text">
          <section v-if="words" class="work-poem room-words" :aria-label="words.label" data-first-view="text">
            <div class="work-poem-version" :lang="shown.lang">
              <div class="work-poem-body" :class="shown.cols">
                <p v-for="(stanza, i) in shown.stanzas" :key="i" class="work-poem-stanza"><span v-for="(line, j) in stanza" :key="j" class="room-line">{{ line }}</span></p>
              </div>
            </div>
            <p class="room-words-foot">
              <span class="work-poem-cite">{{ words.cite }}</span>
              <button v-if="words.alt" type="button" class="room-lang" :aria-pressed="showAlt ? 'true' : 'false'" @click="showAlt = !showAlt">{{ showAlt ? words.backLabel : words.altLabel }}</button>
            </p>
            <p v-if="shown.note" class="work-poem-note">{{ shown.note }}</p>
          </section>
          <section v-if="contextLines.length" class="room-context" :data-first-view="words ? null : 'text'">
            <h2 class="work-section-label">{{ zh ? '這首歌去過的地方' : 'Where this piece has been' }}</h2>
            <p v-for="(line, i) in contextLines" :key="i" :lang="zh ? 'zh-Hant' : 'en'">{{ line }}</p>
          </section>
        </div>
      </section>
    </template>
  </work-record>
  <article v-else class="work-record"><h1>{{ zh ? '找不到這首歌' : 'Track not found' }}</h1><router-link :to="local('/music/')">{{ zh ? '回到音樂' : 'Back to music' }}</router-link></article>
</template>
<script setup>
// 單曲頁（2026-10-04；哲宇：「音樂有沒有可能也有單獨頁面～就像是作品一樣，從 streetvoice 納入我們的體系裡，但是也都連得回去」）。
// 資料＝src/data/music-tracks.json（scripts/build-music-tracks.mjs 產生；StreetVoice 快照＋附詩＋脈絡＋封面）。
// 版面借作品頁的 WorkRecord：頁首、側欄資料、詩區塊（poem，跟作品頁同一套排法）、延伸閱讀、前後頁；
// 頁首與頁首下方換成「聽歌的房間」（2026-10-04 第二版；哲宇：「現在看起來偏空…封面不一定要那麼大，現在詩掉到下面去了～
// 我希望第一眼要能看完詩跟封面跟可以聽音樂」）：歌名、封面（原比例、縮小但仍是主角）、播放器、整首詩或註記同一屏。規則 → docs/MUSIC.md
import { withSlash } from '@/runtime/site-path.mjs'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import data from '@/data/music-tracks.json'
import works from '@/data/work-catalog.json'
import WorkRecord from '@/components/work/WorkRecord.vue'
import TrackPlayer from '@/components/music/TrackPlayer.vue'
import { trackEvent } from '@/runtime/analytics.mjs'

const route = useRoute(), zh = computed(() => route.path.startsWith('/zh/')), local = p => withSlash((zh.value ? '/zh' : '') + p)
const tracks = data.tracks
const track = computed(() => tracks.find(t => t.slug === route.params.slug))
// 時間順（曲名日期，沒有就用發表日；同一天依 StreetVoice 編號）：上一首＝更早寫的，下一首＝之後寫的
const chrono = [...tracks].sort((a, b) => (a.date || a.published).localeCompare(b.date || b.published) || a.id - b.id)
// 顯示名：原曲名以日期碼開頭的都帶日期碼（build-music-tracks.mjs 的 name）；Impromptu 五首沒有
const nameOf = t => (zh.value ? t.name?.zh || t.title.zh || t.title.en : t.name?.en || t.title.en || t.title.zh)
const otherName = t => (zh.value ? (t.title.zh && t.title.en) || '' : (t.title.en && t.title.zh) || '')
const impromptu = t => /impromptu/i.test(t.collection || '')
const day = d => { const [y, m, dd] = d.split('-').map(Number); return zh.value ? `${y} 年 ${m} 月 ${dd} 日` : new Date(Date.UTC(y, m - 1, dd)).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) }
const clock = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
const length = computed(() => track.value ? (zh.value ? `${Math.floor(track.value.seconds / 60)} 分 ${track.value.seconds % 60} 秒` : clock(track.value.seconds)) : '')
const portrait = computed(() => track.value?.cover && track.value.cover.height > track.value.cover.width * 1.05)
const coverAlt = computed(() => track.value ? (zh.value ? `〈${nameOf(track.value)}〉的封面` : `Cover of ${nameOf(track.value)}`) : '')
// 封面的小事實：只寫資料裡有的——照片圖庫比對到同一張原檔才有拍攝日與城市（不寫是誰拍的：有幾張他自己在畫面裡）；幾首歌共用同一張就照實說
const coverNote = computed(() => {
  const c = track.value?.cover
  if (!c) return ''
  const out = []
  if (c.photo?.date) out.push(zh.value ? `封面照片拍於 ${day(c.photo.date)}${c.photo.place ? '，' + c.photo.place.zh : ''}` : `Cover photo taken on ${day(c.photo.date)}${c.photo.place ? ', ' + c.photo.place.en : ''}`)
  if (c.shared > 1) out.push(zh.value ? `這張封面 ${c.shared} 首歌共用（${c.sharedYears}）` : `${c.shared} tracks share this cover (${c.sharedYears})`)
  return out.length ? out.join(zh.value ? '。' : '. ') + (zh.value ? '。' : '.') : ''
})
const card = t => ({ title: nameOf(t), cover: t.cover?.thumb, year: (t.date || t.published).slice(0, 4), path: local('/music/' + t.slug) })
const listen = platform => trackEvent('music_listen_click', { platform, track: track.value?.title.original })
// 大標：日期碼與歌名同一行，日期碼是標題的一部分（等寬數字、淡一階），不是眉標
const titleCode = computed(() => { const t = track.value, n = record.value.title || ''; return t?.code && n.startsWith(t.code + ' ') ? t.code : '' })
const titleRest = computed(() => (titleCode.value ? (record.value.title || '').slice(titleCode.value.length + 1) : record.value.title || ''))
const titleLang = computed(() => (/[\u3400-\u9fff]/.test(titleRest.value) ? 'zh-Hant' : 'en'))
// 歌名長（中文一字算 2）就把字級降一階，換行不把詩擠出第一屏
const titleLong = computed(() => [...titleRest.value].reduce((w, ch) => w + (/[\u3400-\u9fff\uff00-\uffef]/.test(ch) ? 2 : 1), 0) > 18)
const audio = computed(() => track.value?.audio || null)   // build-music-tracks 從 music-audio.json 只帶 src 與 seconds 進來
const artwork = computed(() => { const c = track.value?.cover; return c ? [{ src: c.jpg, sizes: '1200x1200', type: 'image/jpeg' }, { src: c.src, sizes: `${c.width}x${c.height}`, type: 'image/webp' }] : [] })
const upNext = computed(() => {
  const t = track.value
  const n = t && chrono[chrono.findIndex(x => x.id === t.id) + 1]
  return n ? { slug: n.slug, title: nameOf(n), path: local('/music/' + n.slug), cover: n.cover?.thumb } : undefined
})
// 封面：原比例不裁；桌機最長邊 360，手機縮圖最長邊 104（寬度用 CSS 變數給，圖還沒載入版面也不跳）
const coverBox = computed(() => {
  const c = track.value?.cover
  if (!c) return {}
  const r = c.width / c.height
  return { '--cover-w': Math.round(360 * Math.min(1, r)) + 'px', '--cover-w-m': Math.round(104 * Math.min(1, r)) + 'px', '--cover-ratio': `${c.width} / ${c.height}` }
})

// 脈絡：只放資料裡有出處的事實（music-context.json）——配樂給了哪件作品、在哪場演出、發行到哪裡；沒有就整段不出現
const contextLines = computed(() => {
  const t = track.value
  if (!t) return []
  const Z = zh.value
  const lines = []
  for (const r of t.relations) if (r.text) lines.push(r.text[Z ? 'zh' : 'en'])
  for (const p of t.performances) lines.push(p.text[Z ? 'zh' : 'en'])
  if (impromptu(t)) lines.push(Z ? `${day(t.date)}和 ${t.featuring.join('、')} 的即興，收在 StreetVoice 合輯〈${t.playlists.find(p => /impromptu/i.test(p.name))?.name || t.collection}〉。` : `An improvisation with ${t.featuring.join(' and ')} on ${day(t.date)}, collected in the StreetVoice playlist ${t.playlists.find(p => /impromptu/i.test(p.name))?.name || t.collection}.`)
  for (const s of t.streaming) lines.push(Z ? `${s.year} 年發行到 ${s.name}${s.title !== t.title.en ? `（曲名〈${s.title}〉）` : ''}。` : `Released on ${s.name} in ${s.year}${s.title !== t.title.en ? ` as ${s.title}` : ''}.`)
  return lines
})

// 他發歌時附的字（詩或註記）：整首放在第一屏，用作品頁 poem 區塊的字體與節奏，不收合。
// 中文頁放中文、英文頁放他自己的英譯（沒有英譯就放原文並說明）；另一語言用一顆小按鈕切換。
// 欄數：桌機超過 12 行排兩欄、手機超過 14 行排兩欄（段落不拆欄，所以至少要兩段）
const clean = st => (Array.isArray(st) ? st : []).map(s => s.map(l => String(l ?? '')).filter(l => l.trim())).filter(s => s.length)
const showAlt = ref(false)
watch(() => route.path, () => { showAlt.value = false })
const words = computed(() => {
  const t = track.value
  if (!t?.text) return null
  const Z = zh.value, poem = t.text.kind === 'poem'
  const zhS = clean(t.text.stanzas.zh), enS = clean(t.text.stanzas.en)
  if (!zhS.length && !enS.length) return null
  const version = (lang, stanzas) => {
    const lines = stanzas.reduce((n, s) => n + s.length, 0)
    const cols = [stanzas.length > 1 && lines > 12 ? 'room-cols-d2' : '', stanzas.length > 1 && lines > 14 ? 'room-cols-m2' : ''].filter(Boolean).join(' ')
    return { lang, stanzas, cols }
  }
  const zhV = zhS.length ? version('zh-Hant', zhS) : null, enV = enS.length ? version('en', enS) : null
  const main = Z ? zhV || enV : enV || { ...zhV, note: poem ? 'He posted this poem in Chinese only; it is not translated here.' : '' }
  const alt = Z ? (zhV && enV) : (enV && zhV)
  return {
    main, alt,
    label: Z ? (poem ? '附詩' : '附記') : (poem ? 'Poem' : 'Note'),
    cite: Z ? `吳哲宇，${day(t.published)}${poem ? '與曲子一起發在 StreetVoice' : '發歌時附的話'}` : `Che-Yu Wu, ${poem ? 'posted on StreetVoice with the song' : 'the note posted with the song'}, ${day(t.published)}`,
    altLabel: Z ? '看作者英譯' : 'Chinese original',
    backLabel: Z ? '回到中文' : 'Back to English',
  }
})
const shown = computed(() => (words.value ? (showAlt.value && words.value.alt) || words.value.main : null))

const record = computed(() => {
  const t = track.value
  if (!t) return {}
  const Z = zh.value
  const i = chrono.findIndex(x => x.id === t.id)
  // 詩、註記與「這首歌去過的地方」都搬進第一屏的房間（上面的 words、contextLines）；WorkRecord 只負責第一屏以下：
  // 歌曲資料、延伸閱讀、繼續聽、前後的歌
  const blocks = []

  const facts = [
    // 曲名上的日期（他的命名：241104＝2024-11-04）與 StreetVoice 發表日同一天就只列一次
    ...(t.date && t.date !== t.published ? [{ label: Z ? (t.dateBasis === 'title' ? '曲名日期' : '錄音日期') : (t.dateBasis === 'title' ? 'Date in title' : 'Recorded'), value: day(t.date) }] : []),
    { label: Z ? (t.date === t.published ? '日期（StreetVoice 發表）' : 'StreetVoice 發表') : (t.date === t.published ? 'Date (on StreetVoice)' : 'On StreetVoice'), value: day(t.published), href: t.url },
    { label: Z ? '長度' : 'Length', value: clock(t.seconds) },
    ...(t.genre ? [{ label: Z ? '曲風標示' : 'Genre tag', value: t.genre }] : []),
    ...(t.featuring.length ? [{ label: Z ? '合作' : 'With', value: t.featuring.join(Z ? '、' : ', ') }] : []),
    ...t.playlists.map(p => ({ label: Z ? '合輯' : 'Playlist', value: `${p.name}（${p.position}/${p.count}）`.replace(/（(\d+)\/(\d+)）/, Z ? '（第 $1 首，共 $2 首）' : ' (track $1 of $2)'), href: p.url })),
    ...t.streaming.map(s => ({ label: s.name, value: s.title, href: s.url })),
    { label: Z ? '原始曲名' : 'Original title', value: t.title.original },
  ]
  const links = [
    { url: t.url, label: Z ? `StreetVoice：〈${t.title.original}〉` : `StreetVoice: ${t.title.original}` },
    ...t.streaming.map(s => ({ url: s.url, label: `${s.name}：${s.title}`.replace('：', Z ? '：' : ': ') })),
    ...t.relations.map(r => ({ url: local(r.path), label: Z ? `${r.type === 'work' ? '作品' : '展覽'}${r.title.zh}` : `${r.type === 'work' ? 'Work' : 'Exhibition'}: ${r.title.en}` })),
    ...t.performances.map(p => ({ url: local(p.path), label: Z ? `演出${p.title.zh}（${p.year}）` : `Performance: ${p.title.en} (${p.year})` })),
    ...t.lab.map(l => ({ url: local(l.path.split('#')[0]) + '#' + l.path.split('#')[1], label: Z ? `實驗卷宗 ${l.year}：${l.title.zh}` : `Lab archive ${l.year}: ${l.title.en}` })),
    ...t.playlists.map(p => ({ url: p.url, label: Z ? `StreetVoice 合輯：${p.name}` : `StreetVoice playlist: ${p.name}` })),
  ].filter((l, k, all) => all.findIndex(o => o.url === l.url) === k)

  // 繼續聽：先是用了這首歌的作品，再來同一組（同一個展場輪播的歌）、同一張合輯裡前後的歌、同一年的歌；最多四張
  const rel = []
  const push = x => { if (x && rel.length < 4 && !rel.some(r => r.path === x.path)) rel.push(x) }
  for (const r of t.relations.filter(r => r.type === 'work')) { const w = works.find(w => w.slug === r.slug); if (w) push({ title: Z ? (w.titleZh && w.titleZh !== w.title ? w.titleZh : w.title) : w.title, cover: w.cover, year: String(w.year || ''), path: local('/work/' + w.slug) }) }
  for (const g of new Set(t.relations.map(r => r.group).filter(Boolean))) for (const o of tracks) if (o.id !== t.id && o.relations.some(r => r.group === g)) push(card(o))
  for (const p of t.playlists) {
    const mates = tracks.filter(o => o.id !== t.id && o.playlists.some(q => q.id === p.id)).sort((a, b) => Math.abs(a.playlists.find(q => q.id === p.id).position - p.position) - Math.abs(b.playlists.find(q => q.id === p.id).position - p.position))
    for (const o of mates.slice(0, 2)) push(card(o))
  }
  for (const o of chrono.filter(o => o.id !== t.id && (o.date || o.published).slice(0, 4) === t.year).sort((a, b) => Math.abs(chrono.indexOf(a) - i) - Math.abs(chrono.indexOf(b) - i))) push(card(o))

  return {
    zh: Z, title: nameOf(t), subtitle: [otherName(t), impromptu(t) ? `Impromptu / feat. ${t.featuring.join(', ')}` : ''].filter(Boolean).join(Z ? '　' : ' · '),
    category: Z ? (impromptu(t) ? '音樂 · 即興' : '音樂 · 原創曲') : (impromptu(t) ? 'Music · Impromptu' : 'Music'), year: t.year,
    facts, blocks, links, related: rel,
    previous: i > 0 ? { title: nameOf(chrono[i - 1]), path: local('/music/' + chrono[i - 1].slug) } : undefined,
    next: i < chrono.length - 1 ? { title: nameOf(chrono[i + 1]), path: local('/music/' + chrono[i + 1].slug) } : undefined,
    backPath: local('/music/') + '#tracks',
    labels: { back: Z ? '回到音樂' : 'Back to music', related: Z ? '繼續聽' : 'Keep listening', previous: Z ? '上一首（更早）' : 'Earlier piece', next: Z ? '下一首（之後）' : 'Later piece', neighbours: Z ? '前後的歌' : 'Adjacent pieces', contents: Z ? '這首歌' : 'This piece', details: Z ? '目錄與歌曲資料' : 'Contents & track details' },
  }
})
</script>
<style>
/* 聽歌的房間（2026-10-04 第二版）。全部掛在 .music-room 底下，不影響作品頁；字體、顏色沿用 WorkRecord（--work-paper／--work-muted／--gold）。
   桌機：左欄封面＋播放器＋日期長度曲風＋外連，右欄眉標＋歌名＋整首詩。手機：封面縮圖＋歌名一列 → 整寬播放列 → 詩。 */
.music-room.work-record {
  padding-top: 20px;
}
.music-room .room-back {
  padding: 0 0 14px;
}
.music-room .room-back .work-back {
  display: inline-block;
  margin: 0;
  padding: 6px 0;
}
.music-room .room {
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
  grid-template-rows: auto 1fr;
  grid-template-areas: "side head" "side text";
  column-gap: clamp(40px, 5vw, 80px);
  align-items: start;
  margin: 0 0 72px;
}
.music-room .room-head {
  grid-area: head;
  padding: 2px 0 26px;
}
.music-room .room-head .work-kicker {
  margin-bottom: 12px;
}
.music-room .room-head h1,
.music-room .room-head h1:lang(zh-Hant) {
  font-size: clamp(36px, 3.4vw, 50px);
  line-height: 1.18;
  max-width: none;
}
.music-room .room-head h1:lang(zh-Hant) {
  letter-spacing: 0.02em;
}
.music-room .room-head h1.room-title-long {
  font-size: clamp(30px, 2.7vw, 40px);
}
.music-room .room-code {
  font-family: var(--font-body);
  font-weight: 300;
  font-size: 0.66em;
  font-variant-numeric: tabular-nums lining-nums;
  letter-spacing: 0.04em;
  color: var(--work-muted);
  white-space: pre;
  margin-right: 0.1em;
}
.music-room .room-head .work-subtitle {
  margin-top: 8px;
  font-size: 17px;
}
.music-room .room-side {
  grid-area: side;
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: sticky;
  top: 88px;
}
.music-room .room-cover {
  margin: 0;
}
.music-room .room-cover img {
  display: block;
  width: min(100%, var(--cover-w, 360px));
  height: auto;
  aspect-ratio: var(--cover-ratio, 1);
  background: #050505;
}
.music-room .room-when {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin: 0;
  font-size: 14px;
  color: var(--work-muted);
}
.music-room .room-when time {
  color: var(--work-paper);
}
.music-room .room-links {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  margin: -8px 0 0;
  font-size: 14px;
}
.music-room .room-links a {
  text-decoration: underline;
  text-decoration-color: rgba(242, 238, 229, 0.3);
}
.music-room .room-cover-note {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: var(--work-muted);
}
.music-room .room-text {
  grid-area: text;
  min-width: 0;
  max-width: 920px;
}
.music-room .room-words {
  margin: 0;
}
.music-room .room-words .work-poem-body {
  font-size: clamp(17px, 1.3vw, 19px);
  line-height: 1.7;
}
.music-room .room-words .work-poem-version[lang="en"] .work-poem-body {
  font-size: clamp(16px, 1.2vw, 18px);
  line-height: 1.6;
}
.music-room .room-cols-d2 {
  columns: 2;
  column-gap: 56px;
}
.music-room .room-line {
  display: block;
  text-wrap: balance;
  padding-left: 1em;
  text-indent: -1em;
}
.music-room .room-note .work-poem-body,
.music-room.work-record .room-note .work-poem-stanza {
  font-size: clamp(19px, 1.5vw, 22px);
}
.music-room .room-words-foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin: 4px 0 0;
}
.music-room .room-lang {
  padding: 4px 12px;
  border: 1px solid rgba(242, 238, 229, 0.22);
  border-radius: 999px;
  background: transparent;
  color: var(--work-paper);
  font-size: 12px;
  cursor: pointer;
}
.music-room .room-lang:hover {
  border-color: var(--gold);
  color: var(--gold);
}
.music-room .room-words + .room-context {
  margin-top: 36px;
  padding-top: 24px;
  border-top: 1px solid #38362f;
  max-width: 720px;
}
.music-room .room-words + .room-context p {
  font-family: var(--font-body);
  font-size: 15px;
  line-height: 1.75;
  color: var(--work-muted);
}
.music-room .room-context h2 {
  margin: 0 0 14px;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 400;
}
.music-room .room-context p {
  margin: 0 0 12px;
  font-family: var(--font-display);
  font-size: clamp(18px, 1.4vw, 21px);
  line-height: 1.7;
}
/* 第一屏以下：收緊間距（原本作品頁的節奏是給長文的）。歌曲資料從側欄改成一條橫排，延伸閱讀接在下面，不留側欄撐出來的空洞 */
.music-room .work-reading-layout {
  display: block;
}
.music-room .work-reading-layout > div[aria-hidden="true"],
.music-room .work-reading-layout > .work-reading:empty {
  display: none;
}
.music-room .work-record-aside {
  position: static;
  max-height: none;
  overflow: visible;
  padding: 22px 0 8px;
  border-top: 1px solid #38362f;
  margin-bottom: 32px;
}
.music-room .work-record-aside nav {
  display: none;
}
.music-room .work-record-aside dl {
  display: block;
  columns: 4 200px;
  column-gap: 40px;
}
.music-room .work-record-aside dt {
  margin-top: 0;
  break-after: avoid;
}
.music-room .work-record-aside dd {
  break-before: avoid;
  break-inside: avoid;
  margin-bottom: 14px;
}
.music-room .work-reading {
  max-width: 1080px;
}
.music-room .work-reading h2 {
  font-size: clamp(26px, 2.2vw, 34px);
  margin-bottom: 14px;
}
.music-room .work-source-list {
  columns: 2 360px;
  column-gap: 48px;
}
.music-room .work-source-list li {
  break-inside: avoid;
  padding: 12px 0;
  font-size: 14px;
}
.music-room .work-text-section {
  margin-bottom: 48px;
}
.music-room .work-contact {
  padding: 24px 0 32px;
}
.music-room .work-related {
  padding-top: 32px;
  margin-top: 16px;
}
.music-room .work-related-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
}
.music-room .work-related-grid img {
  height: auto;
  aspect-ratio: 1;
  object-fit: cover;
}
.music-room .work-related-grid h3 {
  font-size: 19px;
}
.music-room .work-neighbours {
  margin-top: 40px;
}
@media (max-width: 1100px) {
  .music-room .room {
    grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  }
}
@media (max-width: 991px) {
  .music-room.work-record {
    padding-top: 6px;
  }
  .music-room .room-back {
    padding-bottom: 6px;
  }
  .music-room .room-back .work-back {
    padding: 4px 0;
    font-size: 12px;
  }
  /* 手機：左右欄拆開（display: contents），重新排成「縮圖＋歌名」→ 播放列 → 詩 → 外連 */
  .music-room .room {
    grid-template-columns: auto minmax(0, 1fr);
    grid-template-rows: none;
    grid-template-areas:
      "cover head"
      "cover when"
      "player player"
      "text text"
      "links links"
      "note note";
    column-gap: 16px;
    margin-bottom: 48px;
  }
  .music-room .room-side {
    display: contents;
  }
  .music-room .room-cover {
    grid-area: cover;
    align-self: start;
  }
  .music-room .room-cover img {
    width: var(--cover-w-m, 104px);
  }
  .music-room .room-head {
    padding: 0;
    align-self: end;
  }
  .music-room .room-head .work-kicker {
    display: none;
  }
  .music-room .room-head h1,
  .music-room .room-head h1:lang(zh-Hant) {
    font-size: clamp(24px, 6.6vw, 30px);
    line-height: 1.22;
  }
  .music-room .room-head h1.room-title-long {
    font-size: clamp(20px, 5.4vw, 24px);
  }
  .music-room .room-code {
    font-size: 0.62em;
  }
  .music-room .room-head .work-subtitle {
    margin-top: 2px;
    font-size: 14px;
  }
  .music-room .room-when {
    grid-area: when;
    align-self: start;
    margin-top: 6px;
    gap: 0 10px;
    font-size: 12px;
  }
  .music-room .room-player {
    grid-area: player;
    margin: 14px 0 16px;
  }
  .music-room .room-text {
    grid-area: text;
  }
  .music-room .room-links {
    grid-area: links;
    margin: 20px 0 0;
  }
  .music-room .room-cover-note {
    grid-area: note;
    margin-top: 10px;
  }
  .music-room .room-cols-d2 {
    columns: 1;
  }
  .music-room .room-cols-m2 {
    columns: 2;
    column-gap: 14px;
  }
  .music-room .room-words .work-poem-body {
    font-size: 15px;
    line-height: 1.6;
  }
  .music-room .room-words .work-poem-version[lang="en"] .work-poem-body {
    font-size: 14px;
    line-height: 1.5;
  }
  .music-room .room-cols-m2.work-poem-body {
    font-size: 13.5px;
  }
  .music-room .work-poem-version[lang="en"] .room-cols-m2.work-poem-body {
    font-size: 13px;
    line-height: 1.45;
  }
  .music-room.work-record .room-words .work-poem-stanza {
    margin-bottom: 0.7em;
  }
  .music-room .room-note .work-poem-body,
  .music-room.work-record .room-note .work-poem-stanza {
    font-size: 18px;
  }
  .music-room .room-context p {
    font-size: 17px;
  }
  .music-room .room-words + .room-context {
    margin-top: 24px;
    padding-top: 18px;
  }
  .music-room .room-words + .room-context p {
    font-size: 14px;
  }
  .music-room .work-related-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px 16px;
  }
  .music-room .work-related-grid img {
    height: auto;
  }
  .music-room .work-related-grid h3 {
    font-size: 16px;
  }
}
@media (max-width: 480px) {
  .music-room .work-related-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .music-room .work-related-grid img {
    height: auto;
  }
}
</style>
